import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  InputNumber,
  Tabs,
  Card,
  message,
  Spin,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Typography,
  Radio,
  Popconfirm
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  ProjectOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  TableOutlined
} from "@ant-design/icons";
import axios from "axios";
import UniversalModuleModal from "./UniversalModuleModal";
import { MODULE_MODAL_CONFIGS } from "./moduleModalConfigs";

const { Title, Text } = Typography;

const CostBudgeting = ({ fileId, fileName, setSelectedFile, initialConfig }) => {
  const [data, setData] = useState([]);
  const [structure, setStructure] = useState("1"); // "1" = Predecessors, "2" = Start/End Nodes
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("input");
  const [showSetupModal, setShowSetupModal] = useState(false);

  // Initialize or re-configure table based on modal or loaded file
  useEffect(() => {
    if (fileId) {
      loadFile(fileId);
    } else if (initialConfig) {
      applyModalConfig(initialConfig);
    } else {
      // Default 4 activities if no fileId or initialConfig
      applyModalConfig({
        number_of_activities: 4,
        structure: "1",
        rowNames: ["A", "B", "C", "D"]
      });
    }
  }, [fileId, initialConfig]);

  const applyModalConfig = (config) => {
    if (!config) return;
    const struct = String(config.structure || "1");
    setStructure(struct);

    const count = parseInt(
      config.number_of_activities ||
        config.numTasks ||
        config.rowCount ||
        (config.rowNames ? config.rowNames.length : 4),
      10
    );

    // Generate names (A, B, C... or Custom Names)
    const names =
      config.rowNames && config.rowNames.length >= count
        ? config.rowNames
        : Array.from({ length: count }, (_, i) => String.fromCharCode(65 + i));

    const newRows = Array.from({ length: count }, (_, i) => {
      const actName = names[i] || `Activity ${i + 1}`;
      if (struct === "1") {
        return {
          key: i,
          activity: actName,
          time: 0,
          cost: 0,
          predecessors: ""
        };
      } else {
        return {
          key: i,
          activity: actName,
          start_node: i + 1,
          end_node: i + 2,
          time: 0,
          cost: 0
        };
      }
    });

    setData(newRows);
    setResults(null);
    setActiveTab("input");
  };

  const loadFile = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`/api/retrieve_cost_budgeting/${id}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { input_data, output_data, chart_url } = response.data;
      const loadedStructure = String(input_data.structure || "1");
      setStructure(loadedStructure);

      const loadedActivities = (input_data.activities || []).map((act, index) => {
        if (loadedStructure === "1") {
          const preds = Array.isArray(act.predecessors)
            ? act.predecessors.join(", ")
            : (act.predecessors || "");
          return {
            key: index,
            activity: act.activity || act.name || `Activity ${index + 1}`,
            time: act.time ?? act.activity_time ?? 0,
            cost: act.cost ?? 0,
            predecessors: preds
          };
        } else {
          return {
            key: index,
            activity: act.activity || act.name || `Activity ${index + 1}`,
            start_node: act.start_node ?? index + 1,
            end_node: act.end_node ?? index + 2,
            time: act.time ?? act.activity_time ?? 0,
            cost: act.cost ?? 0
          };
        }
      });

      setData(loadedActivities);
      setResults({ output_data, chart_url });
      setActiveTab("output");
    } catch (error) {
      console.error("Load file error:", error);
      message.error("Failed to load Cost Budgeting file.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndSolve = async () => {
    if (!data || data.length === 0) {
      message.warning("Please add at least one activity.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");

      const formattedActivities = data.map((item) => {
        if (structure === "1") {
          const predStr = typeof item.predecessors === "string" ? item.predecessors : "";
          const predArray = predStr
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

          return {
            activity: item.activity || "Activity",
            time: parseFloat(item.time) || 0,
            cost: parseFloat(item.cost) || 0,
            predecessors: predArray
          };
        } else {
          return {
            activity: item.activity || "Activity",
            start_node: parseInt(item.start_node, 10) || 0,
            end_node: parseInt(item.end_node, 10) || 0,
            time: parseFloat(item.time) || 0,
            cost: parseFloat(item.cost) || 0
          };
        }
      });

      const payload = {
        name: fileName || "Cost Budgeting Analysis",
        input_data: {
          structure: structure,
          activities: formattedActivities
        }
      };

      let response;
      if (fileId) {
        response = await axios.put(`/api/update_cost_budgeting/${fileId}/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post("/api/save_cost_budgeting/", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (setSelectedFile && response.data?.id) {
          setSelectedFile((prev) => ({ ...prev, id: response.data.id }));
        }
      }

      setResults(response.data);
      setActiveTab("output");
      message.success("Cost Budgeting calculated & saved successfully!");
    } catch (error) {
      console.error("Calculation Error:", error.response?.data);
      const errMsg = error.response?.data?.error || "Failed to calculate Cost Budgeting.";
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index][field] = value;
    setData(newData);
  };

  const handleAddRow = () => {
    const newIndex = data.length;
    const newRow =
      structure === "1"
        ? {
            key: newIndex,
            activity: String.fromCharCode(65 + newIndex) || `Activity ${newIndex + 1}`,
            time: 0,
            cost: 0,
            predecessors: ""
          }
        : {
            key: newIndex,
            activity: String.fromCharCode(65 + newIndex) || `Activity ${newIndex + 1}`,
            start_node: newIndex + 1,
            end_node: newIndex + 2,
            time: 0,
            cost: 0
          };
    setData([...data, newRow]);
  };

  const handleDeleteRow = (index) => {
    if (data.length <= 1) {
      message.warning("At least one activity is required.");
      return;
    }
    const newData = data.filter((_, i) => i !== index);
    const rekeyedData = newData.map((row, i) => ({ ...row, key: i }));
    setData(rekeyedData);
  };

  const handleStructureChange = (e) => {
    const newStruct = e.target.value;
    setStructure(newStruct);

    const convertedData = data.map((item, idx) => {
      if (newStruct === "1") {
        return {
          key: idx,
          activity: item.activity || `Activity ${idx + 1}`,
          time: item.time || 0,
          cost: item.cost || 0,
          predecessors: item.predecessors || ""
        };
      } else {
        return {
          key: idx,
          activity: item.activity || `Activity ${idx + 1}`,
          start_node: item.start_node || idx + 1,
          end_node: item.end_node || idx + 2,
          time: item.time || 0,
          cost: item.cost || 0
        };
      }
    });
    setData(convertedData);
  };

  // Editable Input Table Columns
  const inputColumns = [
    {
      title: "Activity Name",
      dataIndex: "activity",
      key: "activity",
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={(e) => updateRow(index, "activity", e.target.value)}
          placeholder="e.g. A"
        />
      )
    }
  ];

  if (structure === "1") {
    inputColumns.push({
      title: "Immediate Predecessors",
      dataIndex: "predecessors",
      key: "predecessors",
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={(e) => updateRow(index, "predecessors", e.target.value)}
          placeholder="e.g. A, B or leave blank"
        />
      )
    });
  } else {
    inputColumns.push(
      {
        title: "Start Node",
        dataIndex: "start_node",
        key: "start_node",
        width: 140,
        render: (text, record, index) => (
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={text}
            onChange={(val) => updateRow(index, "start_node", val)}
          />
        )
      },
      {
        title: "End Node",
        dataIndex: "end_node",
        key: "end_node",
        width: 140,
        render: (text, record, index) => (
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={text}
            onChange={(val) => updateRow(index, "end_node", val)}
          />
        )
      }
    );
  }

  inputColumns.push(
    {
      title: "Activity Time",
      dataIndex: "time",
      key: "time",
      width: 150,
      render: (text, record, index) => (
        <InputNumber
          min={0}
          step={1}
          style={{ width: "100%" }}
          value={text}
          onChange={(val) => updateRow(index, "time", val)}
        />
      )
    },
    {
      title: "Direct Cost ($)",
      dataIndex: "cost",
      key: "cost",
      width: 170,
      render: (text, record, index) => (
        <InputNumber
          min={0}
          step={10}
          style={{ width: "100%" }}
          value={text}
          onChange={(val) => updateRow(index, "cost", val)}
        />
      )
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record, index) => (
        <Popconfirm
          title="Delete activity?"
          onConfirm={() => handleDeleteRow(index)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  );

  // Helper for rendering Budget Tables (Table 2 & Table 3)
  const renderBudgetGrid = (budgetData, titleText, cardKey) => {
    if (!budgetData || !budgetData.periods) return null;

    const { periods = [], activity_rows = [], total_in_period = [], cumulative = [] } = budgetData;

    const columns = [
      {
        title: "Activity",
        dataIndex: "activity",
        key: "activity",
        fixed: "left",
        width: 180,
        render: (val, record) => {
          if (record.isTotal || record.isCumulative) {
            return <strong>{val}</strong>;
          }
          return val;
        }
      },
      ...periods.map((p, idx) => ({
        title: `${p}`,
        dataIndex: `p_${p}`,
        key: `p_${p}`,
        width: 70,
        align: "right",
        render: (val) => {
          if (val === null || val === undefined || val === "") return "";
          const num = typeof val === "number" ? val : parseFloat(val);
          return isNaN(num) ? "" : num.toFixed(2);
        }
      }))
    ];

    // Activity rows
    const tableData = activity_rows.map((rowItem, rIdx) => {
      const row = { key: `act_${rIdx}`, activity: rowItem.activity };
      periods.forEach((p, pIdx) => {
        row[`p_${p}`] = rowItem.values ? rowItem.values[pIdx] : null;
      });
      return row;
    });

    // Total in Period row
    const totalRow = { key: "total_row", activity: "Total in Period", isTotal: true };
    periods.forEach((p, pIdx) => {
      totalRow[`p_${p}`] = total_in_period[pIdx];
    });
    tableData.push(totalRow);

    // Cumulative from start row
    const cumulativeRow = { key: "cum_row", activity: "Cumulative from start", isCumulative: true };
    periods.forEach((p, pIdx) => {
      cumulativeRow[`p_${p}`] = cumulative[pIdx];
    });
    tableData.push(cumulativeRow);

    return (
      <Card title={titleText} key={cardKey} style={{ marginTop: 24 }}>
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          scroll={{ x: "max-content" }}
          size="small"
          bordered
        />
      </Card>
    );
  };

  // Render Output Results Tab (3 Tables + 5 Graphs)
  const renderResults = () => {
    if (!results || !results.output_data) return null;
    const { output_data, chart_url } = results;
    const {
      project_duration,
      total_cost,
      total_time,
      activities = [],
      budget_early = {},
      budget_late = {}
    } = output_data;

    // Table 1: Project Management Results Columns
    const table1Columns = [
      { title: "Activity", dataIndex: "activity", key: "activity", render: (t, r) => r.isProjectHeader ? <strong>{t}</strong> : t }
    ];

    if (structure === "1") {
      table1Columns.push({
        title: "Predecessors",
        dataIndex: "predecessors",
        key: "predecessors",
        render: (val, r) => {
          if (r.isProjectHeader) return "";
          return Array.isArray(val) ? val.join(", ") : (val || "");
        }
      });
    } else {
      table1Columns.push(
        { title: "Start Node", dataIndex: "start_node", key: "start_node", render: (v, r) => r.isProjectHeader ? "" : v },
        { title: "End Node", dataIndex: "end_node", key: "end_node", render: (v, r) => r.isProjectHeader ? "" : v }
      );
    }

    table1Columns.push(
      { title: "Activity Time", dataIndex: "time", key: "time", render: (v, r) => r.isProjectHeader ? <strong>{v}</strong> : v },
      { title: "Activity Cost ($)", dataIndex: "cost", key: "cost", render: (v, r) => r.isProjectHeader ? <strong>${v}</strong> : (v ? `$${v}` : "$0") },
      { title: "Early Start", dataIndex: "ES", key: "ES", render: (v, r) => r.isProjectHeader ? "" : v },
      { title: "Early Finish", dataIndex: "EF", key: "EF", render: (v, r) => r.isProjectHeader ? "" : v },
      { title: "Late Start", dataIndex: "LS", key: "LS", render: (v, r) => r.isProjectHeader ? "" : v },
      { title: "Late Finish", dataIndex: "LF", key: "LF", render: (v, r) => r.isProjectHeader ? "" : v },
      { title: "Slack", dataIndex: "slack", key: "slack", render: (v, r) => r.isProjectHeader ? "" : v },
      {
        title: "Status",
        key: "is_critical",
        render: (_, record) => {
          if (record.isProjectHeader) return null;
          return record.is_critical ? (
            <Tag color="red">CRITICAL</Tag>
          ) : (
            <Tag color="blue">Non-Critical</Tag>
          );
        }
      }
    );

    // Summary Top Row ("Project")
    const projectSummaryRow = {
      key: "project_header",
      activity: "Project",
      start_node: "",
      end_node: "",
      predecessors: "",
      time: project_duration,
      cost: total_cost,
      ES: "",
      EF: "",
      LS: "",
      LF: "",
      slack: "",
      isProjectHeader: true
    };

    const table1Data = [projectSummaryRow, ...activities];

    // Render 5 Graphs
    const render5Graphs = () => {
      if (!chart_url) return null;

      const chartList = [
        { title: "1. Precedence Network Diagram", src: chart_url.precedence_graph, key: "precedence" },
        { title: "2. Early Start Gantt Chart", src: chart_url.gantt_early, key: "gantt_early" },
        { title: "3. Late Start Gantt Chart", src: chart_url.gantt_late, key: "gantt_late" },
        { title: "4. Combined Gantt Chart", src: chart_url.gantt_combined, key: "gantt_combined" },
        { title: "5. Budget vs Time Curve", src: chart_url.budget_vs_time, key: "budget_vs_time" }
      ].filter((item) => item.src);

      return (
        <Card title="Project Diagrams & Charts (5 Graphs)" style={{ marginTop: 24 }}>
          <Tabs
            defaultActiveKey="precedence"
            items={chartList.map((c) => ({
              key: c.key,
              label: c.title,
              children: (
                <div style={{ textAlign: "center", padding: "16px" }}>
                  <img
                    src={c.src}
                    alt={c.title}
                    style={{
                      maxWidth: "100%",
                      borderRadius: 8,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                    }}
                  />
                </div>
              )
            }))}
          />
        </Card>
      );
    };

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* KPI Summary Header */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Project Completion Duration"
                value={project_duration}
                suffix="Periods / Time"
                prefix={<ClockCircleOutlined style={{ color: "#1890ff" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Project Direct Cost"
                value={total_cost}
                precision={2}
                prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Activity Work Time"
                value={total_time}
                prefix={<ProjectOutlined style={{ color: "#fa8c16" }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Table 1: Project Management Results */}
        <Card title="Table 1: Project Management Results (CPM Schedule)">
          <Table
            dataSource={table1Data}
            columns={table1Columns}
            pagination={false}
            rowKey="key"
            bordered
            size="middle"
          />
        </Card>

        {/* Table 2: Early Start Budget Table */}
        {renderBudgetGrid(budget_early, "Table 2: Early Start Budget Table", "early_budget")}

        {/* Table 3: Late Start Budget Table */}
        {renderBudgetGrid(budget_late, "Table 3: Late Start Budget Table", "late_budget")}

        {/* 5 Graphs / Charts */}
        {render5Graphs()}
      </Space>
    );
  };

  return (
    <Spin spinning={loading} tip="Calculating Cost Budgeting results...">
      <Card
        title={
          <Space>
            <BarChartOutlined style={{ color: "#1890ff" }} />
            <span>{fileName || "Project Management - Cost Budgeting"}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setShowSetupModal(true)}
            >
              Setup Modal
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleSaveAndSolve}
            >
              Solve & Save
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "input",
              label: "Data Input",
              children: (
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  {/* Format Selector Bar */}
                  <Card size="small" style={{ background: "#fafafa" }}>
                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                      <div>
                        <Text strong style={{ marginRight: 12 }}>Network Format:</Text>
                        <Radio.Group value={structure} onChange={handleStructureChange}>
                          <Radio.Button value="1">Structure 1: Predecessor List</Radio.Button>
                          <Radio.Button value="2">Structure 2: Start / End Nodes</Radio.Button>
                        </Radio.Group>
                      </div>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddRow}
                      >
                        Add Activity Row
                      </Button>
                    </Space>
                  </Card>

                  {/* Input Data Table */}
                  <Table
                    dataSource={data}
                    columns={inputColumns}
                    pagination={false}
                    rowKey="key"
                    bordered
                    size="middle"
                  />
                </Space>
              )
            },
            {
              key: "output",
              label: "Results & Graphs (3 Tables & 5 Graphs)",
              disabled: !results,
              children: renderResults()
            }
          ]}
        />

        {/* Setup Modal using UniversalModuleModal */}
        <UniversalModuleModal
          isVisible={showSetupModal}
          config={MODULE_MODAL_CONFIGS.cost_budgeting}
          onClose={() => setShowSetupModal(false)}
          onConfirm={(values) => {
            applyModalConfig(values);
            setShowSetupModal(false);
          }}
        />
      </Card>
    </Spin>
  );
};

export default CostBudgeting;
